import { useMemo } from 'react';
import {
  Alert,
  Box,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { usePreciosConceptosVisita } from '@/erp/orden-servicio/orden-servicio-visita/components/otros-conceptos/usePreciosConceptosVisita';
import type { OntParaFormulario } from './ont-cliente.actions';
import { CobroOntForm, montosCobroOnt } from './ont-cobro.actions';

const ACCENT = '#1565c0';

/** Único método que admite diferir a meses, igual que en "Otros conceptos". */
const METODO_DIFERIDO = 'FACTURA';

/**
 * Métodos que no llevan precio: la ONT se entrega canjeando puntos, así que no
 * hay valor que cobrar. El rubro igual se envía a Softnet, en 0, para que quede
 * constancia de lo entregado.
 */
const METODOS_SIN_COBRO = ['FIBRAPUNTOS'];

type Props = {
  /** ONT elegida del stock del técnico; sin ella no se pinta nada. */
  ont?: OntParaFormulario | null;
  value?: CobroOntForm | null;
  onChange: (v: CobroOntForm) => void;
  disabled?: boolean;
};

/**
 * Cobro de la ONT que se le entrega al cliente en un TRASLADO.
 *
 * Aparece debajo de "ONT a Utilizar" cuando la ONT NO es la del cliente: es el
 * caso en que no conserva la suya (perdida, dañada o robada) y hay que
 * entregarle una del stock del técnico y cobrársela.
 *
 * Los métodos de pago y el IVA salen del mismo catálogo que los demás conceptos
 * del traslado (Catálogos › Administración › Traslados). El precio lo pone
 * Activaciones. Al guardar, esta ONT queda registrada como un concepto más, así
 * que genera su rubro en Softnet al finalizar y se descuenta del inventario del
 * técnico, por el mismo camino ya probado de "Otros conceptos".
 */
export default function CobroOntNuevaSection({
  ont,
  value,
  onChange,
  disabled = false,
}: Props) {
  const { metodosPago, ivaPorcentaje } = usePreciosConceptosVisita('TRASLADO');

  const tipoPago = value?.tipo_pago || '';
  const valorSinIva = Number(value?.valor_sin_iva) || 0;
  const mesesPago = value?.meses_pago ?? null;

  const montos = useMemo(
    () => montosCobroOnt(valorSinIva, ivaPorcentaje),
    [valorSinIva, ivaPorcentaje],
  );

  const esSinCobro = METODOS_SIN_COBRO.includes(tipoPago.toUpperCase());

  const cuotaMensual =
    tipoPago.toUpperCase() === METODO_DIFERIDO && (mesesPago || 0) > 0
      ? montos.valor_total / (mesesPago as number)
      : null;

  if (!ont) return null;

  const serie = ont.series?.[0] || '';

  const actualizar = (patch: Partial<CobroOntForm>) => {
    const siguiente: CobroOntForm = {
      tipo_pago: tipoPago,
      valor_sin_iva: valorSinIva,
      meses_pago: mesesPago,
      ...patch,
    };
    // Los meses solo existen si el pago se difiere.
    if (siguiente.tipo_pago.toUpperCase() !== METODO_DIFERIDO) {
      siguiente.meses_pago = null;
    } else if (siguiente.meses_pago == null) {
      siguiente.meses_pago = 1;
    }
    // Pagado con puntos: no se cobra valor.
    if (METODOS_SIN_COBRO.includes(siguiente.tipo_pago.toUpperCase())) {
      siguiente.valor_sin_iva = 0;
    }
    onChange(siguiente);
  };

  return (
    <Grid item xs={12}>
      <Paper variant="outlined" sx={{ p: 2, borderColor: ACCENT }}>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="subtitle2" color={ACCENT}>
              Cobro de la ONT entregada
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {ont.name}
            </Typography>
          </Box>

          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={5}>
              <TextField
                select
                fullWidth
                size="small"
                required
                label="Método de pago"
                value={tipoPago}
                disabled={disabled}
                onChange={(e) => actualizar({ tipo_pago: e.target.value })}
                helperText={!tipoPago ? 'Elija cómo se cobra la ONT' : ' '}
              >
                {(tipoPago && !metodosPago.includes(tipoPago)
                  ? [tipoPago, ...metodosPago]
                  : metodosPago
                ).map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {!esSinCobro &&
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Precio sin IVA"
                inputProps={{ min: 0, step: '0.01' }}
                value={valorSinIva}
                disabled={disabled}
                onChange={(e) => {
                  const n = parseFloat(e.target.value);
                  actualizar({ valor_sin_iva: isNaN(n) || n < 0 ? 0 : n });
                }}
                helperText=" "
              />
            </Grid>
            }

            {!esSinCobro && tipoPago.toUpperCase() === METODO_DIFERIDO && (
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Meses a diferir"
                  inputProps={{ min: 1, step: 1 }}
                  value={mesesPago ?? 1}
                  disabled={disabled}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    actualizar({ meses_pago: isNaN(n) || n < 1 ? 1 : n });
                  }}
                  helperText={
                    cuotaMensual != null
                      ? `$${cuotaMensual.toFixed(2)} por mes`
                      : ' '
                  }
                />
              </Grid>
            )}
          </Grid>

          {!esSinCobro &&
            <Typography variant="body2">
              IVA ({ivaPorcentaje}%): <b>${montos.iva.toFixed(2)}</b> · Total:{' '}
              <b>${montos.valor_total.toFixed(2)}</b>
            </Typography>
          }

          <Alert severity="info" sx={{ py: 0.5 }}>
            {esSinCobro
              ? `Se entrega canjeando ${tipoPago}: no se cobra valor. El rubro
                 viaja a Softnet en 0 para dejar constancia, y la serie ${serie}
                 se descuenta igual del stock del técnico.`
              : `Se cobrará al finalizar el traslado, como un concepto más, y la
                 serie ${serie} se descontará del stock del técnico.`}
          </Alert>
        </Stack>
      </Paper>
    </Grid>
  );
}
